import { getInvoiceCollectionDueDate } from "@/modules/billing/invoiceCollectionDueDate";
import { useMemo, useState } from "react";
import { FileText, Plus } from "lucide-react";
import { Link } from "react-router";
import { useGetList, type Identifier } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AddTask } from "@/components/atomic-crm/tasks/AddTask";
import { CalendarReminderDialog } from "@/modules/calendar/CalendarReminderDialog";
import { ContactCalendarEventsSection } from "@/modules/calendar/ProjectCalendarEventsList";
import { TaskQuickFilterMenu } from "@/components/atomic-crm/tasks/TaskQuickFilterMenu";
import { TaskTable } from "@/components/atomic-crm/tasks/TaskTable";
import {
  countTasksByQuickFilter,
  filterTasksByQuickFilter,
  type TaskQuickFilter,
} from "@/components/atomic-crm/tasks/taskQuickFilters";
import { Note } from "@/components/atomic-crm/notes/Note";
import { findDealLabel } from "@/components/atomic-crm/deals/deal";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import type {
  Company,
  Contact,
  ContactNote,
  Deal,
} from "@/components/atomic-crm/types";
import type { ClientInvoice, Contract, Proposal } from "@/modules/types";
import {
  formatBillingDate,
  invoiceDisplayStatus,
  invoiceStatusTone,
} from "@/modules/billing/billingDisplayUtils";
import { invoiceStatusSidebarLabel } from "@/modules/billing/invoiceStatusSidebarLabel";
import { invoiceListTotals } from "@/modules/billing/invoicePaymentUtils";
import { BillingStatusPill } from "@/modules/billing/BillingStatusPill";
import type { FormSubmissionV2 } from "@/modules/forms/types";
import { ClientTabEmpty } from "@/modules/clients/ClientContactsTab";
import { formatDateTime } from "@/modules/clients/clientShowUtils";
import { clientTableWrapperClassName } from "@/modules/clients/ClientTabSectionCard";
import { MoneyText } from "@/lib/permissions/MoneyText";
import { DataGridTable, type GridColumn } from "@/modules/shared/dataGrid";
import { isPipelineTransitionNote } from "@/modules/leads/leadFollowUpUtils";
import { PipelineUpdateBadge } from "@/modules/shared/ContactActivityFeed";
import { buildOpenDealsFilter } from "@/modules/deals/openDealFilters";
import { ClientTicketsHub } from "@/modules/tickets/ClientTicketsHub";
import type { ClientTicketScope } from "@/modules/tickets/clientTicketScope";
import {
  clientDealFilter,
  clientRecordFilter,
  contactIdsFilter,
  type ClientScope,
} from "@/modules/clients/clientScope";
import {
  ClientContactTag,
  useContactNames,
} from "@/modules/clients/ClientContactTag";

const TabLoading = () => (
  <div className="space-y-2">
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-10 w-full" />
  </div>
);

export const ClientProjectsTab = ({ scope }: { scope: ClientScope }) => {
  const { dealStages } = useConfigurationContext();

  const { data: deals = [], isPending } = useGetList<Deal>(
    "deals",
    {
      filter: clientDealFilter(scope),
      pagination: { page: 1, perPage: 100 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { staleTime: 30_000 },
  );
  const showContact = scope.kind === "company";
  const names = useContactNames(
    showContact
      ? deals.map((deal) => deal.contact_id ?? deal.contact_ids?.[0])
      : [],
  );

  if (isPending) return <TabLoading />;

  if (deals.length === 0) {
    return (
      <ClientTabEmpty
        message={
          scope.kind === "contact"
            ? "No projects linked to this contact yet."
            : "No projects for this client yet."
        }
      />
    );
  }

  const columns: GridColumn<Deal>[] = [
    {
      id: "name",
      label: "Project",
      type: "text",
      sortable: true,
      filterable: true,
      alwaysVisible: true,
      cell: (deal) => (
        <Link to={`/deals/${deal.id}/show`} className="link-action font-medium">
          {deal.name}
        </Link>
      ),
    },
    ...(showContact
      ? [
          {
            id: "contact",
            label: "Contact",
            type: "text" as const,
            cellClassName: "max-w-[10rem]",
            cell: (row: Deal) => (
              <ClientContactTag
                contactId={row.contact_id ?? row.contact_ids?.[0]}
                names={names}
              />
            ),
          },
        ]
      : []),
    {
      id: "stage",
      label: "Stage",
      type: "text",
      sortable: true,
      filterable: true,
      value: (deal) => findDealLabel(dealStages, deal.stage),
      cellClassName: "text-muted-foreground",
      cell: (deal) => findDealLabel(dealStages, deal.stage),
    },
    {
      id: "amount",
      label: "Amount",
      type: "money",
      sortable: true,
      filterable: true,
      value: (deal) => Number(deal.amount ?? 0),
      cellClassName: "text-muted-foreground",
      cell: (deal) => <MoneyText value={deal.amount} />,
    },
    {
      id: "updated_at",
      label: "Updated",
      type: "date",
      sortable: true,
      filterable: true,
      cellClassName: "text-muted-foreground",
      cell: (deal) => formatDateTime(deal.updated_at),
    },
  ];

  return (
    <div className={clientTableWrapperClassName}>
      <DataGridTable
        tableId="client.projects"
        columns={columns}
        rows={deals}
        rowKey={(deal) => deal.id}
      />
    </div>
  );
};

export const ClientOpenDealsTab = ({
  companyId,
  contactId,
}: {
  companyId?: Company["id"];
  contactId?: Company["id"];
}) => {
  const { dealStages, dealPipelineStatuses } = useConfigurationContext();
  const baseFilter = contactId
    ? { "contact_ids@cs": `{${contactId}}` }
    : { "company_id@eq": companyId };
  const filter = buildOpenDealsFilter(
    dealPipelineStatuses,
    dealStages,
    baseFilter,
  );

  const { data: deals = [], isPending } = useGetList<Deal>(
    "deals",
    {
      filter,
      pagination: { page: 1, perPage: 100 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { staleTime: 30_000, enabled: !!(contactId ?? companyId) },
  );

  if (isPending) return <TabLoading />;

  if (deals.length === 0) {
    return (
      <ClientTabEmpty
        message={
          contactId
            ? "No open projects linked to this contact yet."
            : "No open projects for this company yet."
        }
      />
    );
  }

  const columns: GridColumn<Deal>[] = [
    {
      id: "name",
      label: "Project",
      type: "text",
      sortable: true,
      filterable: true,
      alwaysVisible: true,
      cell: (deal) => (
        <Link to={`/deals/${deal.id}/show`} className="link-action font-medium">
          {deal.name}
        </Link>
      ),
    },
    {
      id: "stage",
      label: "Stage",
      type: "text",
      sortable: true,
      filterable: true,
      value: (deal) => findDealLabel(dealStages, deal.stage),
      cellClassName: "text-muted-foreground",
      cell: (deal) => findDealLabel(dealStages, deal.stage),
    },
    {
      id: "amount",
      label: "Amount",
      type: "money",
      sortable: true,
      filterable: true,
      value: (deal) => Number(deal.amount ?? 0),
      cellClassName: "text-muted-foreground",
      cell: (deal) => <MoneyText value={deal.amount} />,
    },
    {
      id: "updated_at",
      label: "Updated",
      type: "date",
      sortable: true,
      filterable: true,
      cellClassName: "text-muted-foreground",
      cell: (deal) => formatDateTime(deal.updated_at),
    },
  ];

  return (
    <div className={clientTableWrapperClassName}>
      <DataGridTable
        tableId="client.open-projects"
        columns={columns}
        rows={deals}
        rowKey={(deal) => deal.id}
      />
    </div>
  );
};

export const ClientInvoicesTab = ({ scope }: { scope: ClientScope }) => {
  const { data = [], isLoading } = useGetList<ClientInvoice>(
    "client_invoices",
    {
      filter: clientRecordFilter(scope),
      pagination: { page: 1, perPage: 50 },
      sort: { field: "issue_date", order: "DESC" },
    },
    { staleTime: 30_000 },
  );
  const showContact = scope.kind === "company";
  const names = useContactNames(
    showContact ? data.map((invoice) => invoice.contact_id) : [],
  );

  if (isLoading) return <TabLoading />;

  if (data.length === 0) {
    return (
      <ClientTabEmpty
        message={
          scope.kind === "contact"
            ? "No invoices for this contact yet."
            : "No invoices for this client yet. Use New invoice to create one."
        }
      />
    );
  }

  const columns: GridColumn<ClientInvoice>[] = [
    {
      id: "invoice_number",
      label: "Invoice #",
      type: "text",
      sortable: true,
      filterable: true,
      alwaysVisible: true,
      cell: (invoice) => (
        <Link
          to={`/billing/invoices/${invoice.id}/show`}
          className="link-action font-medium"
        >
          {invoice.invoice_number}
        </Link>
      ),
    },
    ...(showContact
      ? [
          {
            id: "contact",
            label: "Contact",
            type: "text" as const,
            cellClassName: "max-w-[10rem]",
            cell: (row: ClientInvoice) => (
              <ClientContactTag contactId={row.contact_id} names={names} />
            ),
          },
        ]
      : []),
    {
      id: "status",
      label: "Status",
      type: "text",
      sortable: true,
      filterable: true,
      value: (invoice) => invoiceDisplayStatus(invoice),
      cell: (invoice) => (
        <BillingStatusPill
          tone={invoiceStatusTone(
            invoiceDisplayStatus(invoice),
            getInvoiceCollectionDueDate(invoice),
          )}
        >
          {invoiceStatusSidebarLabel(
            invoiceDisplayStatus(invoice),
            getInvoiceCollectionDueDate(invoice),
            { isPartial: invoiceListTotals(invoice).isPartial },
          )}
        </BillingStatusPill>
      ),
    },
    {
      id: "issue_date",
      label: "Issue date",
      type: "date",
      sortable: true,
      filterable: true,
      cellClassName: "text-muted-foreground",
      cell: (invoice) => formatBillingDate(invoice.issue_date),
    },
    {
      id: "due_date",
      label: "Due date",
      type: "date",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cellClassName: "text-muted-foreground",
      cell: (invoice) =>
        invoice.due_date ? formatBillingDate(invoice.due_date) : "—",
    },
    {
      id: "ticket_id",
      label: "Ticket",
      type: "text",
      filterable: true,
      cell: (invoice) =>
        invoice.ticket_id ? (
          <Link
            to={`/tickets/${invoice.ticket_id}/show`}
            className="link-action"
          >
            Ticket #{invoice.ticket_id}
          </Link>
        ) : (
          "—"
        ),
    },
    {
      id: "total",
      label: "Total",
      type: "money",
      sortable: true,
      filterable: true,
      align: "right",
      value: (invoice) => invoiceListTotals(invoice).total,
      cellClassName: "text-muted-foreground",
      cell: (invoice) => <MoneyText value={invoiceListTotals(invoice).total} />,
    },
    {
      id: "balance",
      label: "Balance",
      type: "money",
      sortable: true,
      filterable: true,
      align: "right",
      value: (invoice) => invoiceListTotals(invoice).balance,
      cell: (invoice) => (
        <MoneyText value={invoiceListTotals(invoice).balance} />
      ),
    },
  ];

  return (
    <div className={clientTableWrapperClassName}>
      <DataGridTable
        tableId="client.invoices"
        columns={columns}
        rows={data}
        rowKey={(invoice) => invoice.id}
      />
    </div>
  );
};

export const ClientProposalsTab = ({ scope }: { scope: ClientScope }) => {
  const { data = [], isPending } = useGetList<Proposal>(
    "proposals",
    {
      filter: clientRecordFilter(scope),
      pagination: { page: 1, perPage: 50 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { staleTime: 30_000 },
  );
  const showContact = scope.kind === "company";
  const names = useContactNames(
    showContact ? data.map((proposal) => proposal.contact_id) : [],
  );

  if (isPending) return <TabLoading />;

  if (data.length === 0) {
    return (
      <ClientTabEmpty
        message={
          scope.kind === "contact"
            ? "No proposals for this contact yet."
            : "No proposals for this client yet. Use the + button above to create one."
        }
      />
    );
  }

  const columns: GridColumn<Proposal>[] = [
    {
      id: "title",
      label: "Title",
      type: "text",
      sortable: true,
      filterable: true,
      alwaysVisible: true,
      cell: (proposal) => (
        <Link
          to={`/proposals/${proposal.id}/preview`}
          className="link-action font-medium"
        >
          {proposal.title}
        </Link>
      ),
    },
    ...(showContact
      ? [
          {
            id: "contact",
            label: "Contact",
            type: "text" as const,
            cellClassName: "max-w-[10rem]",
            cell: (row: Proposal) => (
              <ClientContactTag contactId={row.contact_id} names={names} />
            ),
          },
        ]
      : []),
    {
      id: "status",
      label: "Status",
      type: "text",
      sortable: true,
      filterable: true,
      cell: (proposal) =>
        proposal.status ? (
          <Badge variant="outline" className="capitalize">
            {proposal.status.replace(/-/g, " ")}
          </Badge>
        ) : (
          "—"
        ),
    },
    {
      id: "amount",
      label: "Amount",
      type: "money",
      sortable: true,
      filterable: true,
      align: "right",
      cellClassName: "text-muted-foreground",
      cell: (proposal) =>
        proposal.amount != null ? <MoneyText value={proposal.amount} /> : "—",
    },
  ];

  return (
    <div className={clientTableWrapperClassName}>
      <DataGridTable
        tableId="client.proposals"
        columns={columns}
        rows={data}
        rowKey={(proposal) => proposal.id}
      />
    </div>
  );
};

export const ClientContractsTab = ({ scope }: { scope: ClientScope }) => {
  const { data = [], isPending } = useGetList<Contract>(
    "contracts",
    {
      filter: clientRecordFilter(scope),
      pagination: { page: 1, perPage: 50 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { staleTime: 30_000 },
  );
  const showContact = scope.kind === "company";
  const names = useContactNames(
    showContact ? data.map((contract) => contract.contact_id) : [],
  );

  if (isPending) return <TabLoading />;

  if (data.length === 0) {
    return (
      <ClientTabEmpty
        message={
          scope.kind === "contact"
            ? "No contracts for this contact yet."
            : "No contracts for this client yet."
        }
      />
    );
  }

  const columns: GridColumn<Contract>[] = [
    {
      id: "title",
      label: "Title",
      type: "text",
      sortable: true,
      filterable: true,
      alwaysVisible: true,
      cell: (contract) => (
        <Link
          to={`/contracts/${contract.id}/show`}
          className="link-action font-medium"
        >
          {contract.title}
        </Link>
      ),
    },
    ...(showContact
      ? [
          {
            id: "contact",
            label: "Contact",
            type: "text" as const,
            cellClassName: "max-w-[10rem]",
            cell: (row: Contract) => (
              <ClientContactTag contactId={row.contact_id} names={names} />
            ),
          },
        ]
      : []),
    {
      id: "status",
      label: "Status",
      type: "text",
      sortable: true,
      filterable: true,
      cell: (contract) =>
        contract.status ? (
          <Badge variant="outline" className="capitalize">
            {contract.status.replace(/-/g, " ")}
          </Badge>
        ) : (
          "—"
        ),
    },
    {
      id: "updated_at",
      label: "Updated",
      type: "date",
      sortable: true,
      filterable: true,
      cellClassName: "text-muted-foreground",
      cell: (contract) => formatDateTime(contract.updated_at),
    },
  ];

  return (
    <div className={clientTableWrapperClassName}>
      <DataGridTable
        tableId="client.contracts"
        columns={columns}
        rows={data}
        rowKey={(contract) => contract.id}
      />
    </div>
  );
};

export const ClientTicketsTab = ({
  companyId,
  contactId,
  scope = "company",
}: {
  companyId?: Company["id"];
  contactId?: Contact["id"];
  scope?: ClientTicketScope;
}) => (
  <ClientTicketsHub companyId={companyId} contactId={contactId} scope={scope} />
);

export const ClientSupportTab = ({
  companyId,
}: {
  companyId: Company["id"];
}) => (
  <div className="space-y-4">
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">Tickets</h3>
      <ClientTicketsTab companyId={companyId} />
    </section>
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">Form submissions</h3>
      <ClientWebFormsTab companyId={companyId} />
    </section>
  </div>
);

export const ClientWebFormsTab = ({
  companyId,
}: {
  companyId: Company["id"];
}) => {
  const { data = [], isPending } = useGetList<FormSubmissionV2>(
    "form_submissions_v2",
    {
      filter: { "company_id@eq": companyId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "submitted_at", order: "DESC" },
    },
    { staleTime: 30_000 },
  );

  if (isPending) return <TabLoading />;

  if (data.length === 0) {
    return (
      <ClientTabEmpty message="No form submissions linked to this client yet." />
    );
  }

  const columns: GridColumn<FormSubmissionV2>[] = [
    {
      id: "id",
      label: "Submission",
      type: "number",
      sortable: true,
      alwaysVisible: true,
      value: (submission) => Number(submission.id),
      cell: (submission) => `#${submission.id}`,
    },
    {
      id: "summary",
      label: "Summary",
      type: "text",
      filterable: true,
      value: (submission) => summarizeSubmission(submission.answers),
      cellClassName: "max-w-md truncate text-muted-foreground",
      cell: (submission) => summarizeSubmission(submission.answers),
    },
    {
      id: "submitted_at",
      label: "Submitted",
      type: "date",
      sortable: true,
      filterable: true,
      cellClassName: "text-muted-foreground",
      cell: (submission) => formatDateTime(submission.submitted_at),
    },
  ];

  return (
    <div className={clientTableWrapperClassName}>
      <DataGridTable
        tableId="client.form-submissions"
        columns={columns}
        rows={data}
        rowKey={(submission) => submission.id}
        trailingHead={<TableHead className="text-right">Detail</TableHead>}
        trailingCell={(submission) => (
          <div className="text-right">
            <Link
              to={`/forms-v2/submissions/${submission.id}`}
              className="link-action text-sm"
            >
              View
            </Link>
          </div>
        )}
      />
    </div>
  );
};

export const ClientFilesTab = ({ companyId }: { companyId: Company["id"] }) => {
  const { data: deals = [], isPending } = useGetList<Deal>(
    "deals",
    {
      filter: { "company_id@eq": companyId },
      pagination: { page: 1, perPage: 20 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { staleTime: 30_000 },
  );

  if (isPending) return <TabLoading />;

  if (deals.length === 0) {
    return (
      <ClientTabEmpty message="Project files will appear here once projects are created." />
    );
  }

  return (
    <div className={clientTableWrapperClassName}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Project</TableHead>
            <TableHead className="text-right">Resources</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deals.map((deal) => (
            <TableRow key={deal.id}>
              <TableCell className="font-medium">{deal.name}</TableCell>
              <TableCell className="text-right">
                <Link
                  to={`/deals/${deal.id}/show?tab=resources`}
                  className="link-action inline-flex items-center gap-1 text-sm"
                >
                  <FileText className="size-4" />
                  Open resources
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const ClientTasksTab = ({
  contactIds,
  primaryContactId,
  hideCreate = false,
}: {
  companyId?: Company["id"];
  contactIds: Identifier[];
  primaryContactId?: Identifier | null;
  /** The Activity composer already creates tasks and meetings. */
  hideCreate?: boolean;
}) => {
  const [quickFilter, setQuickFilter] = useState<TaskQuickFilter>("open");
  const [meetingOpen, setMeetingOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const filter = useMemo(() => contactIdsFilter(contactIds), [contactIds]);

  const contactFilter =
    contactIds.length > 0
      ? { "id@in": `(${contactIds.join(",")})` }
      : undefined;

  const { data: tasks = [], isPending } = useGetList(
    "tasks",
    {
      filter,
      pagination: { page: 1, perPage: 200 },
      sort: { field: "due_date", order: "ASC" },
    },
    { staleTime: 30_000, enabled: contactIds.length > 0 },
  );

  const quickFilterCounts = useMemo(
    () => countTasksByQuickFilter(tasks),
    [tasks],
  );
  const visibleTasks = useMemo(
    () => filterTasksByQuickFilter(tasks, quickFilter),
    [quickFilter, tasks],
  );
  const showCompleted = quickFilter === "completed";

  if (isPending) return <TabLoading />;

  if (contactIds.length === 0) {
    return (
      <ClientTabEmpty message="Add a contact to this client before creating tasks." />
    );
  }

  return (
    <div className="space-y-3">
      {hideCreate ? null : (
        <div className="flex flex-wrap items-center gap-2 [&>div]:my-0">
          {contactIds.length === 1 ? (
            <AddTask contactId={contactIds[0]} display="chip" />
          ) : (
            <AddTask
              selectContact
              contactFilter={contactFilter}
              display="chip"
            />
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-6 gap-1"
            onClick={() => setMeetingOpen(true)}
          >
            <Plus className="size-4" />
            Schedule meeting
          </Button>
        </div>
      )}

      <CalendarReminderDialog
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        dateKey={today}
        variant="meeting"
        initialRecord={{
          contact_id: primaryContactId ?? null,
        }}
      />

      <div className="flex justify-end">
        <TaskQuickFilterMenu
          value={quickFilter}
          onChange={setQuickFilter}
          counts={quickFilterCounts}
        />
      </div>

      <ContactCalendarEventsSection
        contactIds={contactIds}
        showCompleted={showCompleted}
        title={showCompleted ? "Completed events" : "Upcoming events"}
        compact
      />

      <TaskTable
        tasks={visibleTasks}
        compact
        emptyMessage={
          showCompleted ? "No completed tasks yet." : "No tasks in this view."
        }
      />
    </div>
  );
};

export const ClientNotesTab = ({
  contactIds,
  showContact = false,
}: {
  contactIds: Identifier[];
  /** Account view: show which person each note belongs to. */
  showContact?: boolean;
}) => {
  const { data: notes = [], isPending } = useGetList<ContactNote>(
    "contact_notes",
    {
      filter: contactIdsFilter(contactIds),
      pagination: { page: 1, perPage: 50 },
      sort: { field: "date", order: "DESC" },
    },
    { staleTime: 30_000, enabled: contactIds.length > 0 },
  );
  const names = useContactNames(
    showContact ? notes.map((note) => note.contact_id) : [],
  );

  if (contactIds.length > 0 && isPending) return <TabLoading />;

  if (contactIds.length === 0 || notes.length === 0) {
    return <p className="text-sm text-muted-foreground">No notes yet.</p>;
  }

  return (
    <div className="divide-y">
      {notes.map((note) => (
        <div key={note.id} className="py-2">
          {showContact || isPipelineTransitionNote(note) ? (
            <div className="mb-1 flex items-center justify-between gap-2 text-xs">
              {showContact ? (
                <ClientContactTag contactId={note.contact_id} names={names} />
              ) : (
                <span />
              )}
              {isPipelineTransitionNote(note) ? <PipelineUpdateBadge /> : null}
            </div>
          ) : null}
          <Note note={note} resource="contact_notes" compact />
        </div>
      ))}
    </div>
  );
};

const summarizeSubmission = (data?: Record<string, unknown>) => {
  if (!data) return "—";
  const values = Object.values(data)
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
  return values.slice(0, 3).join(" · ") || "—";
};
